import type { Format, ProjectedRecord } from '@/services/projected.service'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { axios } from '@/lib'

import { useQuery } from '@tanstack/vue-query'
import { useSchemaData } from '@/composables/useSchemaData'

export const SELECT_ALL_SILO_LIMIT = 10

// Unified composable: manages search filters, silos filtering (with query param sync),
// and executes the search via vue-query.

export interface SearchSort {
  key: string
  dir: 'asc' | 'desc'
}

export interface SearchFiltersState {
  term: string
  silos: string[]
  page: number
  pageSize: number
  sort?: SearchSort
}

export interface SiloOption {
  value: string
  label: string
  icon?: string
  isResource?: boolean
}

// Module-singleton state so all consumers share the same filters
const selectedSilos: Ref<string[]> = ref([])
const term: Ref<string> = ref('')
const page: Ref<number> = ref(1)
// Default page size used across server-table is 20
const pageSize: Ref<number> = ref(20)
const sortRef: Ref<SearchSort | undefined> = ref(undefined)

function parseSort(input?: string | null): SearchSort | undefined {
  if (!input) return undefined
  const [key, dir] = input.split(':')
  if (!key || (dir !== 'asc' && dir !== 'desc')) return undefined
  return { key, dir }
}

function serializeSort(sort?: SearchSort): string | undefined {
  if (!sort) return undefined
  return `${sort.key}:${sort.dir}`
}

function validateSiloIdsWith(validIds: string[], siloIds: string[]): string[] {
  if (!siloIds || siloIds.length === 0) return []
  return siloIds.filter((id) => validIds.includes(id))
}

function parseSilosFromUrlWith(validIds: string[], query: any): string[] {
  if (!query.silos) return []
  let rawSilos: string[]
  if (Array.isArray(query.silos)) rawSilos = query.silos as string[]
  else if (typeof query.silos === 'string')
    rawSilos = query.silos
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
  else return []
  return validateSiloIdsWith(validIds, rawSilos)
}

function updateSelectedSilosWith(validIds: string[], silos: string[]) {
  const validated = validateSiloIdsWith(validIds, silos)
  selectedSilos.value = validated
  if (silos.length !== validated.length) {
    console.warn(
      'Invalid silo IDs filtered out:',
      silos.filter((id) => !validated.includes(id)),
    )
  }
}

// Build a query string fragment for silos
function buildSiloQueryParam(): string {
  if (selectedSilos.value.length === 0) return ''
  return `silos=${selectedSilos.value.join(',')}`
}

// Route syncing is installed per useSearch() call

const queryObject = computed(() => {
  const obj: Record<string, string> = {}
  if (term.value) obj.term = term.value
  if (selectedSilos.value.length > 0) obj.silos = selectedSilos.value.join(',')
  if (page.value > 1) obj.page = String(page.value)
  // Only include if not default (20)
  if (pageSize.value !== 20) obj.pageSize = String(pageSize.value)
  const s = serializeSort(sortRef.value)
  if (s) obj.sort = s
  return obj
})

interface GetSearchResultsOptions {
  term: string
  skip: number
  limit: number
  sort: string
  silos?: string[]
  timestamp?: number
}

type SearchResponse = {
  Count: number
  Formats: Record<string, Format>
  Results: ProjectedRecord[]
  Silo: string
}

async function getSearchResults(options: GetSearchResultsOptions) {
  const { silos, term = '', timestamp, ...pagination } = options

  const params: Record<string, unknown> = { term, ...pagination }
  if (timestamp) params.timestamp = timestamp

  if (Array.isArray(silos)) {
    const filtered = silos.filter((s) => typeof s === 'string' && s.trim().length > 0)
    if (filtered.length >= 1) params.silos = filtered.join(',')
  }

  const response = await axios.get<SearchResponse>('/api/projected', {
    params,
  })

  return response.data
}

// Unified composable
// Base return shared for both overloads
type UseSearchBaseReturn = {
  term: Ref<string>
  selectedSilos: Ref<string[]>
  page: Ref<number>
  pageSize: Ref<number>
  sort: Ref<SearchSort | undefined>
  queryObject: ComputedRef<Record<string, string>>
  updateFilters: (partial: Partial<SearchFiltersState>) => void
  availableSilos: ComputedRef<SiloOption[]>
  filteredSilos: ComputedRef<SiloOption[]>
  silosLoading: ComputedRef<boolean>
  silosError: Ref<Error | null>
  updateSelectedSilos: (silos: string[]) => void
  validateSiloIds: (silos: string[]) => string[]
  parseSilosFromUrl: (query: any) => string[]
  buildSiloQueryParam: () => string
}

// Overloads to properly type the query return when options are provided
export function useSearch(): UseSearchBaseReturn
export function useSearch(
  overrides: ComputedRef<Partial<GetSearchResultsOptions>> | Partial<GetSearchResultsOptions>,
): UseSearchBaseReturn & ReturnType<typeof useQuery<SearchResponse, Error, SearchResponse>>
export function useSearch(
  overrides?: ComputedRef<Partial<GetSearchResultsOptions>> | Partial<GetSearchResultsOptions>,
): any {
  const route = useRoute()
  const router = useRouter()

  // Schema → silo options (cached by vue-query)
  const {
    data: schemaData,
    isLoading: schemaLoading,
    error: schemaError,
  } = useSchemaData({
    filter: 'isSearchable',
  })
  const availableSilos = computed<SiloOption[]>(() => {
    if (!schemaData.value) return []
    return schemaData.value.Data.map((schemaItem) => ({
      value: schemaItem.Index,
      label: schemaItem.Schemas[0]?.DisplayName || schemaItem.Name,
      icon: schemaItem.Icon || undefined,
      isResource: schemaItem.IsResource,
    }))
  })
  const validSiloIds = computed(() => availableSilos.value.map((s) => s.value))
  const isSelectingAllSilos = (silos: string[]) => {
    const validIds = validSiloIds.value
    if (validIds.length === 0) return false
    if (validIds.length > SELECT_ALL_SILO_LIMIT) return false
    if (!Array.isArray(silos) || silos.length === 0) return false
    if (silos.length !== validIds.length) return false
    const selection = new Set(silos)
    if (selection.size !== validIds.length) return false
    return validIds.every((id) => selection.has(id))
  }
  const filteredSilos = computed(() => {
    return availableSilos.value.filter((s) => selectedSilos.value.includes(s.value))
  })

  // Sync global filters from route now that valid IDs are known
  watch(
    [() => validSiloIds.value, () => route.query],
    ([_, q]) => {
      const query = (q as Record<string, any>) || {}
      term.value = (query.term as string) || ''
      page.value = query.page ? Number(query.page) || 1 : 1
      pageSize.value = query.pageSize ? Number(query.pageSize) || 20 : 20
      sortRef.value = parseSort((query.sort as string) || undefined)
      const parsed = parseSilosFromUrlWith(validSiloIds.value, query)
      if (parsed.length > 0) {
        // Use silos from the URL when provided
        updateSelectedSilosWith(validSiloIds.value, parsed)
      } else if (selectedSilos.value.length === 0 && validSiloIds.value.length > 0) {
        // No silos in URL and none currently selected → default to first available silo
        updateSelectedSilosWith(validSiloIds.value, [validSiloIds.value[0]])
      }
    },
    { immediate: true },
  )

  // Merge provided overrides with global filter state
  const options: ComputedRef<GetSearchResultsOptions> | undefined = overrides
    ? computed(() => {
        const base =
          'value' in (overrides as any)
            ? (overrides as ComputedRef<Partial<GetSearchResultsOptions>>).value || {}
            : (overrides as Partial<GetSearchResultsOptions>)
        const normalizedSilos = Array.isArray(base.silos)
          ? base.silos.filter((id) => typeof id === 'string' && id)
          : [...selectedSilos.value]
        const silosForRequest = isSelectingAllSilos(normalizedSilos) ? undefined : normalizedSilos
        return {
          term: (base.term ?? term.value) || '',
          skip: Number(base.skip ?? 0),
          limit: Number(base.limit ?? pageSize.value),
          sort: String(base.sort ?? serializeSort(sortRef.value) ?? ''),
          silos: silosForRequest,
          timestamp: base.timestamp,
        }
      })
    : undefined

  let queryReturn: ReturnType<typeof useQuery<SearchResponse, Error, SearchResponse>> | undefined

  if (options) {
    const key = computed(() => {
      const o = options.value
      const silosArr = Array.isArray(o.silos) ? [...o.silos].filter(Boolean).sort() : undefined
      return [
        'search',
        {
          term: o.term || '',
          skip: o.skip,
          limit: o.limit,
          sort: o.sort || '',
          silos: silosArr || null,
          timestamp: o.timestamp || null,
        },
      ]
    })

    queryReturn = useQuery<SearchResponse, Error, SearchResponse>({
      queryKey: key,
      queryFn: () => getSearchResults(options.value),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
      keepPreviousData: true,
    })
  }

  // Updater that syncs to route with validated silos
  function updateFilters(partial: Partial<SearchFiltersState>) {
    if (partial.term !== undefined) term.value = partial.term
    if (partial.page !== undefined) page.value = partial.page
    if (partial.pageSize !== undefined) pageSize.value = partial.pageSize
    if (partial.sort !== undefined) sortRef.value = partial.sort
    if (partial.silos !== undefined) updateSelectedSilosWith(validSiloIds.value, partial.silos)

    const nextQuery = { ...queryObject.value, ts: String(Date.now()) }
    if (route.path === '/search') router.replace({ path: '/search', query: nextQuery })
    else router.push({ path: '/search', query: nextQuery })
  }

  return {
    // Search query (present only when overrides provided)
    ...(queryReturn || {}),
    // Global search/filter state
    term,
    selectedSilos,
    page,
    pageSize,
    sort: sortRef,
    queryObject,
    updateFilters,
    // Silo helpers and data
    availableSilos,
    filteredSilos,
    silosLoading: computed(() => schemaLoading.value),
    silosError: schemaError,
    updateSelectedSilos: (silos: string[]) => updateSelectedSilosWith(validSiloIds.value, silos),
    validateSiloIds: (silos: string[]) => validateSiloIdsWith(validSiloIds.value, silos),
    parseSilosFromUrl: (query: any) => parseSilosFromUrlWith(validSiloIds.value, query),
    buildSiloQueryParam,
  }
}
